import { useSendSMS } from '@hakimo-ui/hakimo/data-access';
import { LocationContact } from '@hakimo-ui/hakimo/types';
import { useState } from 'react';

interface SendArgs {
  message: string;
  fileList: File[];
  locationId?: number;
  locationAlarmId?: string;
  alarmGroupId?: string;
}

export interface SMSFailure {
  contact: LocationContact;
  error: Error;
}

const toError = (reason: unknown): Error =>
  reason instanceof Error ? reason : new Error(String(reason));

export function useSMSFanout() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [failures, setFailures] = useState<SMSFailure[]>([]);
  const mutation = useSendSMS(() => undefined);

  const send = async (
    targets: LocationContact[],
    args: SendArgs
  ): Promise<boolean> => {
    if (targets.length === 0) return false;
    setSending(true);
    const { message, fileList, locationId, locationAlarmId, alarmGroupId } =
      args;
    const alarmId = locationAlarmId ? Number(locationAlarmId) : undefined;

    const results = await Promise.allSettled(
      targets.map((r) =>
        mutation.mutateAsync({
          to_number: r.phoneNumber,
          to_name: r.name,
          message,
          location_id: locationId,
          location_alarm_id: Number.isInteger(alarmId) ? alarmId : undefined,
          alarm_group_id: alarmGroupId,
          snapshots: fileList,
        })
      )
    );

    const nextSent = new Set(sent);
    const newFailures: SMSFailure[] = [];
    results.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        nextSent.add(String(targets[i].id));
      } else {
        newFailures.push({ contact: targets[i], error: toError(res.reason) });
      }
    });

    setSent(nextSent);
    setFailures(newFailures);
    setSending(false);
    return newFailures.length === 0;
  };

  const reset = () => {
    setSent(new Set());
    setFailures([]);
  };

  return { sending, sent, failures, send, reset };
}
